import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  Check, X, Star, Zap, Building2, Users, HardDrive, 
  MessageSquare, Calendar, Palette, Globe, QrCode, MapPin
} from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Pricing Plans - Basic, Premium & White Label"
        description="Choose the perfect MoveMate plan for your removal company. Basic £29/mo, Premium £79/mo, White Label £149/mo. All plans include 14-day free trial."
        url="https://admin.movematebend.co.uk/pricing"
      />
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A73E8] to-[#00C4CC] text-white">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="MoveMate" className="h-10" />
            <span className="text-xl font-bold">MoveMate</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login" className="px-4 py-2 rounded-lg hover:bg-white/10 transition">
              Login
            </Link>
            <Link to="/register-company" className="px-4 py-2 bg-white text-[#1A73E8] rounded-lg font-semibold hover:bg-slate-100 transition">
              Start Free Trial
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">
          Complete Plan Overview
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Clear pricing and capabilities for companies at every stage — from startups to full white-label brands.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Basic Plan */}
          <PlanCard
            name="Basic Plan"
            target="Small moving companies starting out"
            price="29"
            features={[
              { text: "Up to 50 jobs / month", included: true },
              { text: "Basic inventory management", included: true },
              { text: "Standard support", included: true },
              { text: "Mobile app access", included: true },
              { text: "Up to 5 users", included: true },
              { text: "1GB storage", included: true },
            ]}
            limitations={[
              "No white-label features",
              "No custom branding or domains",
              "No advanced reporting",
              "No API access",
              "No QR Sticker Batch Generator",
            ]}
            ctaText="Start Free Trial"
            onCtaClick={() => navigate('/register-company')}
          />

          {/* Premium Plan */}
          <PlanCard
            name="Premium Plan"
            target="Growing companies with higher volume"
            price="79"
            highlight={false}
            features={[
              { text: "Unlimited jobs", included: true },
              { text: "Advanced inventory management", included: true },
              { text: "Priority support", included: true },
              { text: "Mobile app access", included: true },
              { text: "Advanced reporting", included: true },
              { text: "API access", included: true },
              { text: "Up to 25 users", included: true },
              { text: "10GB storage", included: true },
              { text: "QR Sticker Batch Generator", included: true },
              { text: "Google Calendar integration", included: true },
            ]}
            limitations={[
              "No white-label features (Standard branding)",
              "No custom domains",
            ]}
            ctaText="Start Free Trial"
            onCtaClick={() => navigate('/register-company')}
          />

          {/* White Label Plan */}
          <PlanCard
            name="White Label Plan"
            target="Established companies wanting their own brand"
            price="149"
            highlight={true}
            badge="MOST POPULAR"
            features={[
              { text: "Everything in Premium", included: true },
              { text: "Custom branding & colors", included: true },
              { text: "Custom logo & background", included: true },
              { text: "Custom domain support", included: true },
              { text: "White-label mobile app", included: true },
              { text: "Branded QR codes", included: true },
              { text: "Dedicated support", included: true },
              { text: "Up to 50 users", included: true },
              { text: "50GB storage", included: true },
              { text: "Driver Tracking", included: true },
              { text: "AI Inventory Scanner", included: true },
              { text: "SMS Notifications", included: true },
              { text: "In-App Chat System", included: true },
              { text: "Notification Template Management", included: true },
              { text: "Custom SMTP Settings", included: true },
            ]}
            brandingOptions={[
              "Fonts: System, Roboto, Open Sans, Lato, Montserrat, Poppins",
              "Button styles: Rounded, Square, Pill",
              "Card styles: Flat, Elevated, Outlined",
              "Custom app icon & splash screen",
              "Custom CSS support (up to 10,000 characters)",
              "Gradient & accent color options",
            ]}
            ctaText="Start Free Trial"
            onCtaClick={() => navigate('/register-company')}
          />
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="bg-slate-900 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Functional Capabilities by Plan
          </h2>

          {/* Core Functions */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Zap className="text-yellow-400" size={24} />
              Core Business Functions (All Plans)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FeatureItem icon={<Building2 size={20} />} text="Job Management" />
              <FeatureItem icon={<QrCode size={20} />} text="Inventory Management" />
              <FeatureItem icon={<MessageSquare size={20} />} text="Customer Chat" />
              <FeatureItem icon={<Users size={20} />} text="Role-based Access" />
            </div>
          </div>

          {/* Premium Functions */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Star className="text-blue-400" size={24} />
              Premium-Only Functions
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FeatureItem icon={<Zap size={20} />} text="Advanced Analytics" />
              <FeatureItem icon={<Globe size={20} />} text="API Access" />
              <FeatureItem icon={<Calendar size={20} />} text="Google Calendar Sync" />
              <FeatureItem icon={<QrCode size={20} />} text="QR Batch Generator" />
            </div>
          </div>

          {/* White Label Functions */}
          <div>
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Palette className="text-purple-400" size={24} />
              White Label-Only Functions
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FeatureItem icon={<Palette size={20} />} text="Custom Branding" />
              <FeatureItem icon={<Globe size={20} />} text="Custom Domains" />
              <FeatureItem icon={<MapPin size={20} />} text="Driver Tracking" />
              <FeatureItem icon={<QrCode size={20} />} text="Branded QR Codes" />
              <FeatureItem icon={<MessageSquare size={20} />} text="SMS Notifications" />
              <FeatureItem icon={<MessageSquare size={20} />} text="Notification Templates" />
              <FeatureItem icon={<Zap size={20} />} text="Custom SMTP" />
              <FeatureItem icon={<Zap size={20} />} text="AI Inventory Scanner" />
            </div>
          </div>
        </div>
      </section>

      {/* Role-Based Access Table */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Role-Based Access by Plan</h2>
          <p className="text-center text-slate-600 mb-12">
            User Limits: Basic: 5 users · Premium: 25 users · White Label: 50 users
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-center font-semibold">Level</th>
                  <th className="px-4 py-3 text-center font-semibold">Basic</th>
                  <th className="px-4 py-3 text-center font-semibold">Premium</th>
                  <th className="px-4 py-3 text-center font-semibold">White Label</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <RoleRow role="Company Admin" level={40} basic premium whiteLabel />
                <RoleRow role="Operations Manager" level={35} basic premium whiteLabel />
                <RoleRow role="Finance / Accounts" level={30} basic="limited" premium whiteLabel />
                <RoleRow role="Scheduler / Ops Staff" level={25} basic premium whiteLabel />
                <RoleRow role="Customer Service" level={20} basic premium whiteLabel />
                <RoleRow role="Surveyor / Estimator" level={15} basic premium whiteLabel />
                <RoleRow role="Supervisor / Team Lead" level={10} basic premium whiteLabel />
                <RoleRow role="Driver / Porter / Crew" level={5} basic premium whiteLabel />
                <RoleRow role="Customer" level={1} basic="limited" premium="limited" whiteLabel="limited" />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Plan Upgrade Benefits */}
      <section className="bg-gradient-to-r from-[#1A73E8] to-[#00C4CC] text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Plan Upgrade Benefits</h2>
          <p className="text-lg mb-12 text-white/90">
            Move up to unlock higher limits, priority support and branding control.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-left">
              <h3 className="font-bold text-xl mb-4">Basic → Premium</h3>
              <ul className="space-y-2 text-white/90">
                <li>• Unlimited jobs (from 50)</li>
                <li>• Advanced reporting</li>
                <li>• API access</li>
                <li>• Users: 5 → 25</li>
                <li>• Storage: 1GB → 10GB</li>
                <li>• Priority support</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-left">
              <h3 className="font-bold text-xl mb-4">Premium → White Label</h3>
              <ul className="space-y-2 text-white/90">
                <li>• Full branding & custom domain</li>
                <li>• White-label app & QR codes</li>
                <li>• Advanced design options</li>
                <li>• Dedicated support</li>
                <li>• Users: 25 → 50</li>
                <li>• Storage: 10GB → 50GB</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p className="text-xl text-slate-600 mb-8">
          Start your 14-day free trial with the White Label plan. No credit card required.
        </p>
        <Button
          size="lg"
          className="bg-gradient-to-r from-[#1A73E8] to-[#00C4CC] text-white px-8 py-6 h-auto text-lg"
          onClick={() => navigate('/register-company')}
        >
          Start Free Trial
        </Button>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p>&copy; 2025 MoveMate. All rights reserved.</p>
          <p className="mt-2">Powered by Aztrix Media Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

// Plan Card Component
const PlanCard = ({ name, target, price, features, limitations, brandingOptions, ctaText, onCtaClick, highlight, badge }) => (
  <div className={`rounded-2xl p-8 ${highlight ? 'bg-gradient-to-br from-[#1A73E8] to-[#00C4CC] text-white ring-4 ring-[#00C4CC]/50 scale-105' : 'bg-white border-2 border-slate-200'} relative`}>
    {badge && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
        <Star size={14} /> {badge}
      </div>
    )}
    
    <h3 className="text-2xl font-bold mb-2">{name}</h3>
    <p className={`text-sm mb-6 ${highlight ? 'text-white/80' : 'text-slate-600'}`}>{target}</p>
    
    <div className="mb-6">
      <span className="text-4xl font-bold">£{price}</span>
      <span className={`${highlight ? 'text-white/80' : 'text-slate-500'}`}>/month</span>
    </div>

    <div className="space-y-3 mb-6">
      {features.map((feature, i) => (
        <div key={i} className="flex items-center gap-2">
          <Check className={`${highlight ? 'text-white' : 'text-green-500'} flex-shrink-0`} size={18} />
          <span className="text-sm">{feature.text}</span>
        </div>
      ))}
    </div>

    {limitations && limitations.length > 0 && (
      <div className={`space-y-2 mb-6 pt-4 border-t ${highlight ? 'border-white/20' : 'border-slate-200'}`}>
        {limitations.map((limitation, i) => (
          <div key={i} className="flex items-center gap-2">
            <X className={`${highlight ? 'text-white/60' : 'text-slate-400'} flex-shrink-0`} size={16} />
            <span className={`text-sm ${highlight ? 'text-white/70' : 'text-slate-500'}`}>{limitation}</span>
          </div>
        ))}
      </div>
    )}

    {brandingOptions && (
      <div className={`space-y-2 mb-6 pt-4 border-t ${highlight ? 'border-white/20' : 'border-slate-200'}`}>
        <p className="font-semibold text-sm mb-3">Advanced Branding Options:</p>
        {brandingOptions.map((option, i) => (
          <p key={i} className={`text-xs ${highlight ? 'text-white/80' : 'text-slate-600'}`}>
            {option}
          </p>
        ))}
      </div>
    )}

    <Button
      className={`w-full ${highlight ? 'bg-white text-[#1A73E8] hover:bg-slate-100' : 'bg-gradient-to-r from-[#1A73E8] to-[#00C4CC] text-white'}`}
      onClick={onCtaClick}
    >
      {ctaText}
    </Button>
  </div>
);

// Feature Item Component
const FeatureItem = ({ icon, text }) => (
  <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3">
    <span className="text-[#00C4CC]">{icon}</span>
    <span className="text-sm">{text}</span>
  </div>
);

// Role Row Component
const RoleRow = ({ role, level, basic, premium, whiteLabel }) => (
  <tr className="hover:bg-slate-50">
    <td className="px-4 py-3 font-medium">{role}</td>
    <td className="px-4 py-3 text-center text-slate-500">{level}</td>
    <td className="px-4 py-3 text-center">
      {basic === true ? <Check className="text-green-500 mx-auto" size={18} /> : 
       basic === "limited" ? <span className="text-xs text-slate-500">Limited</span> : 
       <X className="text-slate-300 mx-auto" size={18} />}
    </td>
    <td className="px-4 py-3 text-center">
      {premium === true ? <Check className="text-green-500 mx-auto" size={18} /> : 
       premium === "limited" ? <span className="text-xs text-slate-500">Limited</span> : 
       <X className="text-slate-300 mx-auto" size={18} />}
    </td>
    <td className="px-4 py-3 text-center">
      {whiteLabel === true ? <Check className="text-green-500 mx-auto" size={18} /> : 
       whiteLabel === "limited" ? <span className="text-xs text-slate-500">Limited</span> : 
       <X className="text-slate-300 mx-auto" size={18} />}
    </td>
  </tr>
);

export default PricingPage;
